/**
 * API конструктора шаблонов карточек РД.
 */

import { del, get, post, put } from "@/lib/api-client-v2";
import type {
  RdCardTemplateListResponse,
  RdCardTemplateWithDetails,
  RdCardFieldType,
} from "@/types/rd-card-template";
import type {
  CardFieldWidgetTypeItem,
  CardTemplateImportOnConflict,
  CardTemplateImportResponse,
  CardTemplatePublishRequest,
  CardTemplatePublishResponse,
  CardTemplateRestoreRequest,
} from "@/types/card-template";

export interface RdCardTemplateCreatePayload {
  code: string;
  name: string;
  description?: string | null;
  request_type_kls_id?: number | null;
  organization_kls_id?: number | null;
  is_default?: boolean;
}

export interface RdCardTemplateUpdatePayload {
  name?: string;
  description?: string | null;
  request_type_kls_id?: number | null;
  organization_kls_id?: number | null;
  is_default?: boolean;
  is_active?: boolean;
}

export interface RdCardSectionPayload {
  code: string;
  name: string;
  display_order?: number;
  is_collapsible?: boolean;
  is_default_open?: boolean;
  ui_config?: Record<string, unknown>;
}

export interface RdCardSectionUpdatePayload {
  code?: string;
  name?: string;
  display_order?: number;
  is_collapsible?: boolean;
  is_default_open?: boolean;
  ui_config?: Record<string, unknown>;
}

export interface RdCardFieldPayload {
  field_key: string;
  label: string;
  description?: string | null;
  section_id?: number | null;
  field_type: RdCardFieldType;
  qual_id: number;
  field_type_kls_id?: number | null;
  kls_id?: number | null;
  is_required?: boolean;
  is_readonly?: boolean;
  display_order?: number;
  is_active?: boolean;
}

export interface RdCardFieldUpdatePayload {
  field_key?: string;
  label?: string;
  description?: string | null;
  section_id?: number | null;
  field_type?: RdCardFieldType;
  qual_id?: number;
  field_type_kls_id?: number | null;
  kls_id?: number | null;
  is_required?: boolean;
  is_readonly?: boolean;
  display_order?: number;
  is_active?: boolean;
}

export interface RdCardBindingPayload {
  target_schema?: string;
  target_table: string;
  target_column: string;
  binding_kind?: string | null;
}

export const rdCardTemplatesApi = {
  /** Типы полей из KLS (qual FIELD_TYPES), тот же контракт, что /card-templates/meta/field-widget-types. */
  listFieldWidgetTypes: () =>
    get<CardFieldWidgetTypeItem[]>("/rd-card-templates/meta/field-widget-types"),

  list: (params?: {
    status?: string;
    request_type_kls_id?: number;
    global_type_only?: boolean;
    is_active?: boolean;
    skip?: number;
    limit?: number;
  }) => get<RdCardTemplateListResponse>("/rd-card-templates", params ?? {}),

  get: (id: number) => get<RdCardTemplateWithDetails>(`/rd-card-templates/${id}`),

  create: (body: RdCardTemplateCreatePayload) =>
    post<RdCardTemplateWithDetails>("/rd-card-templates", body),

  update: (id: number, body: RdCardTemplateUpdatePayload) =>
    put<RdCardTemplateWithDetails>(`/rd-card-templates/${id}`, body),

  publish: (id: number, body?: CardTemplatePublishRequest) =>
    post<CardTemplatePublishResponse>(`/rd-card-templates/${id}/publish`, body ?? {}),

  archive: (id: number, body?: CardTemplatePublishRequest) =>
    post<CardTemplatePublishResponse>(`/rd-card-templates/${id}/archive`, body ?? {}),

  restore: (id: number, body?: CardTemplateRestoreRequest) =>
    post<CardTemplatePublishResponse>(`/rd-card-templates/${id}/restore`, body ?? {}),

  /** JSON-пакет schema geopulseml.rd_card_template_export v1 */
  exportTemplateJson: (templateId: number) =>
    get<Record<string, unknown>>(`/rd-card-templates/${templateId}/export`),

  exportTemplatesBulk: (templateIds: number[]) =>
    post<Record<string, unknown>>("/rd-card-templates/export", { template_ids: templateIds }),

  importTemplatesJson: (
    bundle: Record<string, unknown>,
    onConflict: CardTemplateImportOnConflict = "error"
  ) => {
    const q = new URLSearchParams({ on_conflict: onConflict });
    return post<CardTemplateImportResponse>(`/rd-card-templates/import?${q.toString()}`, bundle);
  },

  /** Удаление черновика или архивной записи (опубликованный — только после архивации). */
  delete: (id: number) => del<void>(`/rd-card-templates/${id}`),

  /** Новый черновик с копией секций/полей/привязок; `new_code` — query, как у ОИ. */
  clone: (templateId: number, newCode: string) =>
    post<RdCardTemplateWithDetails>(
      `/rd-card-templates/${templateId}/clone?new_code=${encodeURIComponent(newCode)}`,
      {},
    ),

  addSection: (templateId: number, body: RdCardSectionPayload) =>
    post<RdCardTemplateWithDetails>(`/rd-card-templates/${templateId}/sections`, body),

  updateSection: (templateId: number, sectionId: number, body: RdCardSectionUpdatePayload) =>
    put<RdCardTemplateWithDetails>(`/rd-card-templates/${templateId}/sections/${sectionId}`, body),

  deleteSection: (templateId: number, sectionId: number) =>
    del<RdCardTemplateWithDetails>(`/rd-card-templates/${templateId}/sections/${sectionId}`),

  addField: (templateId: number, body: RdCardFieldPayload) =>
    post<RdCardTemplateWithDetails>(`/rd-card-templates/${templateId}/fields`, body),

  updateField: (templateId: number, fieldId: number, body: RdCardFieldUpdatePayload) =>
    put<RdCardTemplateWithDetails>(`/rd-card-templates/${templateId}/fields/${fieldId}`, body),

  deleteField: (templateId: number, fieldId: number) =>
    del<RdCardTemplateWithDetails>(`/rd-card-templates/${templateId}/fields/${fieldId}`),

  upsertBinding: (templateId: number, fieldId: number, body: RdCardBindingPayload) =>
    put<RdCardTemplateWithDetails>(
      `/rd-card-templates/${templateId}/fields/${fieldId}/binding`,
      body,
    ),

  deleteBinding: (templateId: number, fieldId: number) =>
    del<RdCardTemplateWithDetails>(
      `/rd-card-templates/${templateId}/fields/${fieldId}/binding`,
    ),
};
